import { print } from 'graphql'

import { logger } from '@app/common/logger/logger'
import { store } from '@app/common/store'
import { RefreshTokenLandingStatus, UserRoles } from '@app/graphql/types'
import {
  RefreshAuthTokenDocument,
  RefreshAuthTokenMutation,
  RefreshAuthTokenMutationVariables
} from '@app/queries/RefreshAuthToken/RefreshAuthToken.queries'
import SDK from '@app/SDK'

import { acknowledgeRefreshTokenLanding } from './acknowledgeRefreshTokenLanding'
import AuthAction from './actions'
import { publishRefreshedAuthSession } from './authSessionBroadcast'
import { writeAuthSessionRecord } from './authSessionRecord'
import { getClientTabId } from './clientTabId'
import { AuthSession } from './models'
import { withRefreshLock } from './refreshLock'
import { AuthSelectors } from './selectors'
import { syncAuthSession } from './syncAuthSession'

const REFRESH_AUTH_TOKEN_QUERY = print(RefreshAuthTokenDocument)
const RETRY_DELAY_MS = 500
const CONVERGING_REFRESH_LIMIT = 2

const refreshState: { inFlight?: Promise<string | undefined> } = {}

type RotatedSession = AuthSession & { refreshTokenReceipt: string }

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

export const refreshSession = (): Promise<string | undefined> => {
  if (!refreshState.inFlight) {
    refreshState.inFlight = refreshAcrossTabs().finally(() => {
      refreshState.inFlight = undefined
    })
  }

  return refreshState.inFlight
}

const refreshAcrossTabs = (): Promise<string | undefined> => {
  const refreshTokenBeforeLock = AuthSelectors.getRefreshToken(store.getState())
  const lawyerId = AuthSelectors.getUserId(store.getState())

  logger.info(
    `refreshAcrossTabs running for lawyer ${lawyerId} with refreshTokenBeforeLock ${refreshTokenBeforeLock?.slice(
      -4
    )}`
  )

  return withRefreshLock(async () => {
    logger.info(
      `inside the refresh lock of lawyer ${lawyerId}, syncing the auth session with the shared record`
    )
    syncAuthSession(store)

    const recordedRefreshToken = AuthSelectors.getRefreshToken(store.getState())

    logger.info(
      `inside the refresh lock of lawyer ${lawyerId}: comparing recorded refreshToken ${recordedRefreshToken?.slice(
        -4
      )} with refreshTokenBeforeLock ${refreshTokenBeforeLock?.slice(-4)}`
    )

    if (recordedRefreshToken !== refreshTokenBeforeLock) {
      logger.info(
        `inside the refresh lock of lawyer ${lawyerId}: another tab has refreshed during lock. returning current auth token`
      )

      return AuthSelectors.getToken(store.getState()) ?? undefined
    }

    logger.info(`inside the refresh lock of lawyer ${lawyerId}: current tab gets to requestRefresh`)

    return requestRefresh()
  })
}

const requestRefresh = async (hasRetried = false): Promise<string | undefined> => {
  const refreshToken = AuthSelectors.getRefreshToken(store.getState())
  const lawyerId = AuthSelectors.getUserId(store.getState())

  logger.info(
    `inside requestRefresh of lawyer ${lawyerId} with refreshToken ${refreshToken?.slice(-4)}`
  )

  if (!refreshToken) {
    if (AuthSelectors.getToken(store.getState())) {
      store.dispatch(AuthAction.logout())
    }

    return undefined
  }

  const clientTabId = getClientTabId()

  try {
    const rotatedSession = await rotateSession(refreshToken, clientTabId)

    void acknowledgeLanding(rotatedSession, clientTabId).catch((error) => logger.error(error))

    return rotatedSession.token
  } catch (error) {
    if (!hasRetried) {
      await delay(RETRY_DELAY_MS)
      syncAuthSession(store)

      return requestRefresh(true)
    }

    logger.error(error)
    store.dispatch(AuthAction.logout())

    return undefined
  }
}

const rotateSession = async (
  refreshToken: string,
  clientTabId: string
): Promise<RotatedSession> => {
  const lawyerId = AuthSelectors.getUserId(store.getState())

  logger.info(
    `inside requestRefresh of lawyer ${lawyerId} calling RefreshAuthTokenMutation with refreshToken ${refreshToken.slice(
      -4
    )} and clientTabId ${clientTabId}`
  )

  const response = await SDK.serviceManager.graphql<
    RefreshAuthTokenMutationVariables,
    RefreshAuthTokenMutation
  >({
    query: REFRESH_AUTH_TOKEN_QUERY,
    variables: {
      refreshToken,
      clientTabId,
      userId: lawyerId,
      userRole: UserRoles.Lawyer
    }
  })

  if (response.errors || !response.data?.refreshAuthToken) {
    throw new Error(response.errors?.[0]?.message ?? 'Failed to refresh auth token')
  }

  const rotatedSession = response.data.refreshAuthToken
  const session = { token: rotatedSession.token, refreshToken: rotatedSession.refreshToken }

  logger.info(
    `inside requestRefresh of lawyer ${lawyerId}: got new session from RefreshAuthTokenMutation with refreshToken ${session.refreshToken?.slice(
      -4
    )}`
  )

  writeAuthSessionRecord(session)
  publishRefreshedAuthSession(session)

  store.dispatch(AuthAction.onRefreshSuccess(session))

  return rotatedSession
}

const acknowledgeLanding = async (
  rotatedSession: RotatedSession,
  clientTabId: string
): Promise<void> => {
  let landedSession = rotatedSession
  let convergingRefreshes = 0

  while (await isSupersededOnLanding(landedSession, clientTabId)) {
    const supersededRefreshToken = landedSession.refreshToken

    if (convergingRefreshes === CONVERGING_REFRESH_LIMIT) {
      logger.info(
        `refreshToken ${supersededRefreshToken.slice(
          -4
        )} is still superseded after ${convergingRefreshes} converging refreshes, leaving it to the next refresh`
      )

      return
    }

    convergingRefreshes += 1

    logger.info(
      `refreshToken ${supersededRefreshToken.slice(
        -4
      )} was already superseded when it landed, refreshing again to converge on the session head`
    )

    landedSession = await withRefreshLock(() => rotateSession(supersededRefreshToken, clientTabId))
  }
}

const isSupersededOnLanding = async (
  landedSession: RotatedSession,
  clientTabId: string
): Promise<boolean> => {
  logger.info(
    `acknowledging that refreshToken ${landedSession.refreshToken.slice(
      -4
    )} landed, with clientTabId ${clientTabId}`
  )

  const status = await acknowledgeRefreshTokenLanding({
    token: landedSession.token,
    refreshTokenReceipt: landedSession.refreshTokenReceipt,
    clientTabId
  })

  logger.info(
    `the landing of refreshToken ${landedSession.refreshToken.slice(
      -4
    )} with clientTabId ${clientTabId} was acknowledged as ${status}`
  )

  return status === RefreshTokenLandingStatus.AlreadySuperseded
}
